/**
 * Axios client for the fasto_geo service (3D building map backend).
 *
 * fasto_geo verifies the SAME Django JWT the main API uses, so this client
 * reuses the in-memory access token from `@/api/client`. Refresh stays the
 * responsibility of the main client: on a 401 here we ping a cheap Django
 * endpoint through `apiClient` (whose interceptor refreshes + retries), then
 * replay the geo request with the fresh token.
 */
import axios, { type AxiosInstance, type AxiosRequestConfig, type InternalAxiosRequestConfig } from 'axios';
import { apiClient, getAccessToken } from '@/api/client';
import { env } from '@/lib/env';

export const geoClient: AxiosInstance = axios.create({
  baseURL: env.geoApiBase,
  timeout: 30_000,
  headers: { 'Content-Type': 'application/json' },
});

geoClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = getAccessToken();
  if (token && !config.headers.has?.('Authorization')) {
    config.headers.set('Authorization', `Bearer ${token}`);
  }
  return config;
});

geoClient.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config as (InternalAxiosRequestConfig & { _geoRetry?: boolean }) | undefined;
    if (error.response?.status === 401 && original && !original._geoRetry) {
      original._geoRetry = true;
      try {
        // Any authed Django call triggers the main client's refresh flow.
        await apiClient.get('/feature/bi/api/access/');
        const fresh = getAccessToken();
        if (fresh) {
          original.headers.set('Authorization', `Bearer ${fresh}`);
          return geoClient.request(original);
        }
      } catch {
        // fall through to the original rejection
      }
    }
    return Promise.reject(error);
  },
);

export async function geoGet<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
  const r = await geoClient.get<T>(url, config);
  return r.data;
}

export async function geoPost<T>(
  url: string,
  body?: unknown,
  config?: AxiosRequestConfig,
): Promise<T> {
  const r = await geoClient.post<T>(url, body, config);
  return r.data;
}

/**
 * MVT tile URL template for MapLibre ({z}/{x}/{y} placeholders kept literal).
 * Auth is attached per-request by `geoTransformRequest`.
 */
export function geoTileUrlTemplate(params: Record<string, string>): string {
  const qs = new URLSearchParams(params).toString();
  let base = env.geoApiBase || '';
  // MapLibre requires absolute tile URLs; expand the dev-proxy relative base.
  if (base.startsWith('/') && typeof window !== 'undefined') {
    base = `${window.location.origin}${base}`;
  }
  return `${base}/tiles/buildings/{z}/{x}/{y}.mvt${qs ? `?${qs}` : ''}`;
}

/** MapLibre `transformRequest`: adds the Bearer header to fasto_geo requests. */
export function geoTransformRequest(url: string): { url: string; headers?: Record<string, string> } {
  const base = env.geoApiBase || '';
  const isGeo = base
    ? url.startsWith(base) || url.includes('/tiles/buildings/')
    : url.includes('/tiles/buildings/');
  if (!isGeo) return { url };
  const token = getAccessToken();
  return token ? { url, headers: { Authorization: `Bearer ${token}` } } : { url };
}
